import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  Sun, Moon, Linkedin, Github, Mail, GraduationCap,
  Briefcase, Code, Terminal, Layers, Award, Calendar,
  ExternalLink, FileText, Globe, BookOpen, Link as LinkIcon
} from 'lucide-react';
import { resumeData } from './data/resumeData';
import profileImg from './assets/tarang.jpg'

export default function App() {
  const [isDark, setIsDark] = useState(true);
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 768;
    }
    return false;
  });

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const toggleTheme = () => setIsDark(!isDark);

  const glassCard = "bg-white/70 dark:bg-slate-900/50 backdrop-blur-xl border border-slate-300 dark:border-white/10 rounded-[32px] shadow-xl shadow-indigo-500/5";
  const subtleGlass = "bg-white/50 dark:bg-slate-800/50 backdrop-blur-md border border-slate-200 dark:border-white/5 rounded-2xl";

  const fadeInUpVariant = {
    hidden: { opacity: isMobile ? 1 : 0, y: isMobile ? 0 : 20 },
    visible: { 
        opacity: 1, 
        y: 0, 
        transition: isMobile ? { duration: 0 } : { duration: 0.4, ease: "easeOut" } 
    }
  };

  const staggerContainerVar = {
    hidden: { opacity: isMobile ? 1 : 0 },
    visible: {
      opacity: 1,
      transition: isMobile ? { duration: 0, staggerChildren: 0 } : {
        staggerChildren: 0.1,
        delayChildren: 0
      }
    }
  };

  const staggerChildVar = {
    hidden: { opacity: isMobile ? 1 : 0, y: isMobile ? 0 : 20 },
    visible: { 
        opacity: 1, 
        y: 0, 
        transition: isMobile ? { duration: 0 } : { duration: 0.4 } 
    }
  };

  const popInVar = {
    hidden: { opacity: isMobile ? 1 : 0, scale: isMobile ? 1 : 0.8 },
    visible: { 
        opacity: 1, 
        scale: 1, 
        transition: isMobile ? { duration: 0 } : { type: "spring", stiffness: 100 } 
    }
  };

  const blobAnimation = {
    animate: {
        x: [0, 50, -30, 0],
        y: [0, -40, 30, 0],
        scale: [1, 1.05, 0.95, 1]
    },
    transition: {
        duration: 20,
        repeat: Infinity,
        repeatType: "mirror",
        ease: "easeInOut"
    }
  }

  const getLinkIcon = (type) => {
    switch(type) {
        case 'website': return <Globe size={14} />;
        case 'doc': return <FileText size={14} />;
        case 'article': return <ExternalLink size={14} />;
        case 'blog': return <BookOpen size={14} />;
        default: return <LinkIcon size={14} />;
    }
  };

  return (
    <div className={`min-h-screen font-sans selection:bg-indigo-500/30 selection:text-indigo-900 dark:selection:text-white overflow-hidden relative text-slate-900 dark:text-slate-200 transition-colors duration-500`}>

      <div className="fixed inset-0 -z-10 overflow-hidden transition-colors duration-700 bg-slate-50 dark:bg-[#0f172a]">
        
        <motion.div
           variants={blobAnimation}
           animate="animate"
           className="absolute top-[-10%] left-[-20%] w-[700px] h-[700px] rounded-full
            bg-purple-300/30 dark:bg-purple-900/20 blur-[120px]" />
        
        <motion.div
            variants={blobAnimation}
            animate="animate"
            transition={{ delay: 5, ...blobAnimation.transition }}
            className="absolute bottom-[-10%] right-[-20%] w-[600px] h-[600px] rounded-full
             bg-indigo-300/30 dark:bg-blue-900/20 blur-[120px]" />
         
         <motion.div
            animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
            className="absolute top-[30%] left-[40%] w-[400px] h-[400px] rounded-full
             bg-indigo-200/40 dark:bg-indigo-900/10 blur-[150px]" />
      </div>

      <motion.nav
        initial={isMobile ? { y: 0, opacity: 1 } : { y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={isMobile ? { duration: 0 } : { duration: 0.8, ease: "easeOut" }}
        className="fixed w-full z-50 bg-white/70 dark:bg-slate-900/60 backdrop-blur-md border-b border-slate-200 dark:border-white/10 transition-colors duration-500"
      >
        <div className="max-w-6xl mx-auto px-6 h-16 flex justify-between items-center">
          <motion.span whileHover={{ scale: 1.05 }} className="text-2xl font-extrabold bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400 bg-clip-text text-transparent cursor-pointer"><a href="/">TD.</a></motion.span>
          <div className="flex items-center gap-6">
            <div className="hidden md:flex gap-6 text-sm font-semibold tracking-wide text-slate-600 dark:text-slate-200">
              {['About', 'Education', 'Experience', 'Projects'].map((item) => (
                 <a key={item} href={`#${item.toLowerCase()}`} className="relative group hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                    {item}
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 transition-all group-hover:w-full"></span>
                 </a>
              ))}
            </div>
            
            <motion.button 
              whileTap={{ scale: 0.9, rotate: 15 }}
              whileHover={{ scale: 1.1 }}
              onClick={toggleTheme} 
              className={`p-2 rounded-full transition-all duration-300 border shadow-sm
                ${isDark 
                  ? 'bg-slate-800 border-slate-700 text-indigo-400' 
                  : 'bg-white border-slate-300 text-orange-400 hover:text-orange-500'
                }`}
            >
              {isDark ? <Moon size={20} /> : <Sun size={20} />}
            </motion.button>
          </div>
        </div>
      </motion.nav>

      <section id="about" className="pt-36 pb-20 px-6 max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12">
        <motion.div variants={fadeInUpVariant} initial="hidden" animate="visible" className="flex-1 text-center md:text-left z-10">
          <h1 className="text-5xl md:text-7xl font-extrabold mb-4 leading-tight text-slate-900 dark:text-white">
            Tarang <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 dark:from-indigo-400 dark:to-purple-400">Donga</span>
          </h1>
          <h2 className="text-xl md:text-2xl text-indigo-600 dark:text-indigo-200 font-medium mb-6">
            {resumeData.title}
          </h2>
          <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed mb-8 max-w-2xl">
            {resumeData.summary || "Specializing in Software Systems and full-stack architecture, focusing on delivering high-impact projects from concept to launch."}
          </p>
          
          <div className="flex justify-center md:justify-start gap-4">
            <motion.a whileHover={{ scale: 1.1, y: -3 }} whileTap={{ scale: 0.95 }} href={`mailto:${resumeData.contact.email}`} className="p-3 bg-indigo-600 text-white rounded-full transition shadow-lg shadow-indigo-500/30 hover:bg-indigo-700"><Mail size={24} /></motion.a>
            
            <motion.a whileHover={{ scale: 1.1, y: -3 }} whileTap={{ scale: 0.95 }} href={resumeData.contact.linkedin} target="_blank" rel="noreferrer" className="p-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-indigo-600 dark:text-indigo-400 rounded-full transition shadow-sm"><Linkedin size={24} /></motion.a>
            
            <motion.a whileHover={{ scale: 1.1, y: -3 }} whileTap={{ scale: 0.95 }} href={resumeData.contact.github} target="_blank" rel="noreferrer" className="p-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-indigo-600 dark:text-indigo-400 rounded-full transition shadow-sm"><Github size={24} /></motion.a>
            
            <motion.a 
                whileHover={{ scale: 1.1, y: -3 }} 
                whileTap={{ scale: 0.95 }} 
                href={resumeData.contact.resume} 
                target="_blank" 
                rel="noreferrer" 
                className="flex items-center gap-2 px-6 py-3 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-white rounded-full transition shadow-sm font-bold hover:border-indigo-500 dark:hover:border-indigo-400 hover:text-indigo-600 dark:hover:text-indigo-400"
            >
                <FileText size={20} />
                <span>Resume</span>
            </motion.a>
          </div>
        </motion.div>

        <motion.div 
            initial={isMobile ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.5 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={isMobile ? { duration: 0 } : { duration: 0.8, delay: 0.2, type: "spring" }}
            whileHover={{ scale: 1.02, transition: { duration: 0.3 } }}
            className="w-64 h-64 md:w-80 md:h-80 rounded-[36px] bg-gradient-to-br from-indigo-500/30 to-purple-600/30 dark:from-indigo-500/50 dark:to-purple-600/50 p-[2px] backdrop-blur-xl shadow-2xl shadow-indigo-900/10"
        >
          <div className="w-full h-full bg-white/50 dark:bg-slate-900/50 backdrop-blur-md rounded-[34px] overflow-hidden">
            <img
              src={profileImg}
              alt="Tarang Donga"
              className="w-full h-full object-cover"
            />
          </div>
        </motion.div>
      </section>

      <section id="education" className="py-20 px-6 max-w-6xl mx-auto relative z-10">
        <motion.div variants={fadeInUpVariant} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-col items-center mb-16 text-center">
          <h3 className="text-4xl font-extrabold mb-4 flex items-center gap-4 text-slate-900 dark:text-white">
            <GraduationCap size={40} className="text-indigo-600 dark:text-indigo-400" /> Education
          </h3>
            <p className="text-slate-500 dark:text-slate-300 max-w-xl">Academic background from world-class institutions.</p>
        </motion.div>

        <motion.div 
            variants={staggerContainerVar}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid md:grid-cols-2 gap-8"
        >
          {resumeData.education?.map((edu, idx) => (
            <motion.div
              key={idx}
              variants={staggerChildVar}
              whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.2, ease: "easeInOut" } }}
              className={`${glassCard} p-8 relative overflow-hidden group`}
            >
              <div className="absolute top-0 right-0 p-8 opacity-5 dark:opacity-5">
                <GraduationCap size={120} className="text-indigo-600 dark:text-indigo-300" />
              </div>

              <div className="relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h4 className="text-2xl font-bold text-slate-900 dark:text-white mb-3 tracking-tight">{edu.institution}</h4>
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-500/10 dark:bg-indigo-500/20 border border-indigo-500/20 text-indigo-700 dark:text-indigo-200 rounded-full text-sm font-bold backdrop-blur-md">
                      {edu.degree}
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-3 text-slate-700 dark:text-slate-200">
                    <Award size={18} className="text-indigo-500 dark:text-indigo-400 shrink-0" />
                    <span className="text-sm font-medium leading-relaxed">{edu.details}</span>
                  </div>
                  <div className="flex items-center gap-3 text-slate-500 dark:text-slate-400">
                    <Calendar size={18} className="shrink-0" />
                    <span className="text-sm font-medium">{edu.duration}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section className="py-12 px-6 max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-8">
          
          <motion.div variants={fadeInUpVariant} initial="hidden" whileInView="visible" viewport={{ once: true }} className={`${glassCard} p-8`}>
            <h3 className="text-xl font-bold mb-8 flex items-center gap-3 text-slate-900 dark:text-white"><Terminal size={22} className="text-indigo-500 dark:text-indigo-400" /> Languages & Frameworks</h3>
            <motion.div variants={staggerContainerVar} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-wrap gap-3">
              {resumeData.skills?.languagesFrameworks.map((skill, i) => (
                <motion.span 
                    key={i} 
                    variants={popInVar}
                    whileHover={{ scale: 1.1 }}
                    className="px-4 py-2 bg-indigo-50 dark:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-500/20 text-indigo-700 dark:text-indigo-300 rounded-xl text-sm font-medium backdrop-blur-md cursor-default transition-colors hover:bg-indigo-100 dark:hover:bg-indigo-500/30"
                >
                    {skill}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div variants={fadeInUpVariant} initial="hidden" whileInView="visible" viewport={{ once: true, delay: 0.1 }} className={`${glassCard} p-8`}>
            <h3 className="text-xl font-bold mb-8 flex items-center gap-3 text-slate-900 dark:text-white"><Layers size={22} className="text-indigo-500 dark:text-indigo-400" /> Tools & Platforms</h3>
            <motion.div variants={staggerContainerVar} initial="hidden" whileInView="visible" viewport={{ once: true }} className="flex flex-wrap gap-3">
              {resumeData.skills?.toolsPlatforms.map((tool, i) => (
                <motion.span 
                    key={i} 
                    variants={popInVar}
                    whileHover={{ scale: 1.1 }}
                    className="px-4 py-2 bg-white/40 dark:bg-slate-800/50 border border-white/20 dark:border-white/10 text-slate-700 dark:text-slate-300 rounded-xl text-sm font-medium backdrop-blur-md cursor-default transition-colors hover:bg-white/60 dark:hover:bg-white/10"
                >
                    {tool}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section id="experience" className="py-20 px-6 max-w-4xl mx-auto relative z-10">
        <motion.h3 variants={fadeInUpVariant} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-3xl font-bold mb-12 flex items-center gap-3 text-slate-900 dark:text-white"><Briefcase className="text-indigo-600 dark:text-indigo-400" /> Experience</motion.h3>
        
        <motion.div 
            variants={staggerContainerVar}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-12 border-l-2 border-slate-300 dark:border-slate-800 ml-4"
        >
          {resumeData.experience?.map((exp, idx) => (
            <motion.div key={idx} variants={staggerChildVar} className="relative pl-10">
              <div className="absolute -left-[9px] top-1 w-5 h-5 rounded-full bg-indigo-600 border-4 border-slate-50 dark:border-[#0f172a]">
                 <div className="absolute inset-0 rounded-full bg-indigo-400 animate-ping opacity-75"></div>
              </div>

              <motion.div 
                  whileHover={{ x: 5 }}
                  className={`${subtleGlass} p-6 transition-all duration-300 hover:bg-white/60 dark:hover:bg-slate-800/80`}
              >
                  <span className="text-sm font-bold text-indigo-600 dark:text-indigo-300 uppercase tracking-wider">{exp.duration}</span>
                  <h4 className="text-xl font-bold mt-2 text-slate-900 dark:text-white">{exp.title}</h4>
                  <p className="text-slate-600 dark:text-slate-400 mb-4 font-medium">{exp.company} • {exp.location}</p>
                  
                  <ul className="space-y-3 mb-6">
                    {exp.description?.map((bullet, i) => (
                      <li key={i} className="text-slate-700 dark:text-slate-300 flex gap-3 leading-relaxed"><span className="text-indigo-500 dark:text-indigo-400 mt-1">•</span> {bullet}</li>
                    ))}
                  </ul>

                  {(exp.links || exp.images) && (
                    <div className="mb-6 space-y-4">
                        {exp.links && (
                            <div className="flex flex-wrap gap-3">
                                {exp.links.map((link, lIdx) => (
                                    <motion.a 
                                        key={lIdx} 
                                        href={link.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        whileHover={{ scale: 1.1, x: 2 }} 
                                        whileTap={{ scale: 0.95 }}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold text-indigo-600 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-900/20 shadow-sm hover:shadow-md hover:bg-indigo-100 dark:hover:bg-indigo-900/40 border border-indigo-100 dark:border-indigo-500/20 transition-all"
                                    >
                                        {getLinkIcon(link.type)}
                                        {link.label}
                                    </motion.a>
                                ))}
                            </div>
                        )}

                        {exp.images && (
                            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide">
                                {exp.images.map((imgSrc, imgIdx) => (
                                    <div key={imgIdx} className="flex-shrink-0 w-48 h-32 rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 shadow-sm relative group cursor-pointer">
                                        <img src={imgSrc} alt="Work screenshot" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors"></div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2">
                    {exp.tags?.map((tag, i) => (
                      <span key={i} className="text-[11px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 border border-slate-300 dark:border-slate-700 bg-white/50 dark:bg-slate-900/50 px-3 py-1 rounded-lg backdrop-blur-sm">{tag}</span>
                    ))}
                  </div>
              </motion.div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <section id="projects" className="py-20 px-6 max-w-6xl mx-auto relative z-10">
        <motion.h3 variants={fadeInUpVariant} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-3xl font-bold mb-12 flex items-center gap-3 text-slate-900 dark:text-white"><Code className="text-indigo-600 dark:text-indigo-400" /> Featured Projects</motion.h3>
        <motion.div 
             variants={staggerContainerVar}
             initial="hidden"
             whileInView="visible"
             viewport={{ once: true, margin: "-100px" }}
             className="grid md:grid-cols-2 gap-8"
        >
          {resumeData.projects?.map((proj, idx) => (
            <motion.div
                key={idx}
                variants={staggerChildVar}
                whileHover={{ y: -5, scale: 1.02, transition: { duration: 0.2, ease: "easeInOut" } }}
                className={`${glassCard} p-6 group cursor-pointer flex flex-col h-full`}
            >
              <div className="flex justify-between items-start mb-4">
                <h4 className="font-bold text-xl text-slate-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-300 transition flex items-center gap-2">
                    {proj.name}
                </h4>
              </div>
              
              <p className="text-slate-600 dark:text-slate-300 text-sm leading-relaxed mb-6 flex-grow">{proj.description}</p>
              
              <div className="flex justify-between items-center pt-4 border-t border-slate-200 dark:border-white/5 mt-auto">
                <div className="flex gap-3">
                  {proj.tags?.slice(0,3).map((tag, i) => <span key={i} className="text-xs text-indigo-600 dark:text-indigo-300 font-bold bg-indigo-50 dark:bg-indigo-900/30 px-2 py-1 rounded-md backdrop-blur-sm">{tag}</span>)}
                </div>
                <span className="text-xs font-bold text-slate-400 dark:text-slate-500">{proj.duration}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <footer className="py-12 text-center border-t border-slate-200 dark:border-white/10 relative z-10 backdrop-blur-md bg-white/30 dark:bg-slate-900/60">
        <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">© 2025 {resumeData.name}.</p>
      </footer>
    </div>
  );
}